import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { User } from '@sb/remote-control/types';
import type { ToastState } from '@sb/ui/components';
import { Toast } from '@sb/ui/components';
import { ApolloClientProvider } from '@sbrc/components/apollo/ApolloProvider';
import DevPushBanner from '@sbrc/components/dev-push-banner/DevPushBanner';
import IPadTeleopModalBridge from '@sbrc/components/IPadTeleopModalBridge';
import RobotFailureToastBridge from '@sbrc/components/RobotFailureToastBridge';
import VersionMismatchBanner from '@sbrc/components/version-mismatch-banner/VersionMismatchBanner';
import { GlobalContext } from '@sbrc/contexts';
import { useAuthState, useFeatureFlag } from '@sbrc/hooks';
import { onGetUser } from '@sbrc/services';
import { isTabletApp } from '@sbrc/utils/userAgent';

interface AppProviderProps {
  children: React.ReactNode;
  noGraphQl?: boolean;
}

const queryClient = new QueryClient();

const AppProvider = ({ children, noGraphQl }: AppProviderProps) => {
  const { userID } = useAuthState();
  const isAppRedesign = useFeatureFlag('appRedesign');

  const [toast, setToast] = useState<ToastState | null>(null);
  const [settings, setSettings] = useState<User.ConvertedResponse | null>();

  const [visualizerChildren, setVisualizerChildren] = useState<
    Record<string, React.ReactNode>
  >({});

  useEffect(() => {
    setSettings(undefined);

    if (!userID) {
      return undefined;
    }

    const unsubscribe = onGetUser(userID, setSettings);

    return () => unsubscribe();
  }, [userID]);

  return (
    <GlobalContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        settings,
        toast: { state: toast, set: setToast },
        visualizerChildren: {
          state: visualizerChildren,
          set: setVisualizerChildren,
        },
        userID,
      }}
    >
      <QueryClientProvider client={queryClient}>
        {noGraphQl && children}
        {!noGraphQl && <ApolloClientProvider>{children}</ApolloClientProvider>}
        <DevPushBanner />
      </QueryClientProvider>

      {isTabletApp() && <IPadTeleopModalBridge />}

      <RobotFailureToastBridge />

      <VersionMismatchBanner />

      <Toast
        isOpen={Boolean(toast && !toast.isClosed)}
        kind={toast?.kind}
        onClose={() => setToast(toast && { ...toast, isClosed: true })}
        style={
          isAppRedesign && toast?.kind !== 'top' ? { bottom: 115 } : undefined
        }
      >
        {toast?.message}
      </Toast>
    </GlobalContext.Provider>
  );
};

export default AppProvider;
